import { cn, focusRing } from "@simpro/ui";
import { type CSSProperties, Fragment, type ReactNode, useId } from "react";
import { Link } from "react-router";
import { initials } from "@/lib/initials";
import { ArrowDisc } from "@/shared/components/ArrowDisc";
import { useInlineBook } from "@/shared/hooks/useInlineBook";
import { STAGGER_MS } from "@/shared/motion/presets";
import { page } from "@/shared/styles/layout";
import { pill } from "@/shared/styles/pill";
import { useSalonClock } from "../hooks/useSalonClock";
import { openStatus } from "../lib/openingHours";
import type { SalonProfile } from "../schemas/salon.schemas";
import { type HeroCarousel, HeroCarouselControls, type HeroImage, HeroSlides, useHeroCarousel } from "./HeroCarousel";

/** One figure in the row under the buttons. */
export interface HeroStat {
  value: string;
  label: string;
  /** A star after the value, in the accent: the rating. */
  star?: boolean;
}

export interface HomeHeroProps {
  salon: SalonProfile;
  /** The menu's headings, in order; the first three name what the salon does above the headline. */
  categories?: readonly string[];
  /** The first free time today, already written ("12:30 pm"); shown on the "Open" pill. */
  nextFree?: string | null;
  /** "Your last visit", right under the hero for a signed-in customer (round 2 C18). */
  lastVisit?: ReactNode;
  /** What the salon wrote for its hero (FR-SA-191); a blank field keeps the fallback. */
  custom?: HeroCustom | null;
  /** A second picture for the round frame over the arch, from the salon's gallery; none, no frame. */
  inset?: HeroImage | null;
  /** The salon in numbers, at most three shown (FR-SA-190); none, no row. */
  stats?: readonly HeroStat[];
}

/** The hero fields of the landing page's content, as the page passes them in. */
export interface HeroCustom {
  headline: string | null;
  highlight: string | null;
  text: string | null;
  buttonLabel: string | null;
  /** The arch's pictures in order (FR-SA-205); two or more fade from one to the next (FR-SA-206). */
  images: readonly HeroImage[];
}

/** What the page promises when the salon has written nothing of its own. */
const PROMISE =
  "Choose your treatments, pick a time that suits you, and pay at the salon. Booking takes under a minute.";

/**
 * The landing page's first screen, in the owner's Organic Wellness style (design note
 * salon-booking-organic-wellness.md section 6 item 2; TDD-SA FR-SA-004, FR-SA-190, FR-SA-202):
 * - on the left, a sage rule and what the salon does and where; its headline, very large, with the
 *   chosen words in italic in the accent; the promise; the green pill with its sage arrow disc and a
 *   sage-outlined pill to the menu; and the salon in numbers, set large in the display face;
 * - on the right, the salon's pictures in a tall arch over a slowly morphing sage blob, fading from one
 *   to the next with dots and a pause button under the arch when there are several (FR-SA-205,
 *   FR-SA-206), a second picture in a round frame at its foot, and a cream pill with a breathing dot
 *   saying whether it is open.
 *
 * On a phone the two halves stack, words first. Keeping the words on the ground, whatever the picture
 * is, means an odd picture (a logo, say) never costs the headline its contrast.
 *
 * Motion (FR-SA-200): the headline rises word by word, the arch settles from a slight zoom, the round
 * frame arrives last, the blob changes shape and the dot breathes; under reduced motion nothing moves.
 * The buttons are there from the first paint and never wait on an animation.
 *
 * What the salon has written for its hero comes first (FR-SA-191): its headline with the words it
 * chose to highlight, its text, its picture and its button. Each field it left blank falls back:
 * - the headline: the tagline, else the name (design note salon-home-landing.md OQ 2), with the last
 *   word highlighted;
 * - the text: the booking promise;
 * - the pictures: the cover;
 * - the button: "Book an appointment".
 *
 * A salon with no picture at all gets a pale sage arch with its monogram, never an empty frame.
 */
export function HomeHero({ salon, categories = [], nextFree, lastVisit, custom, inset, stats = [] }: HomeHeroProps) {
  const { clock, timeZone } = useSalonClock();
  const { branch } = salon;
  const status = openStatus(salon.hours, salon.upcomingHolidays, clock, timeZone);
  const book = useInlineBook<HTMLAnchorElement>();
  const place = (branch ? [branch.name, branch.city] : []).filter(
    (part, index, all) => part && all.indexOf(part) === index && part !== salon.displayName,
  );
  // "Hair · Skin · Nails · Anna Nagar"; a salon with no menu yet says who and where instead.
  const line = categories.length > 0 ? [...categories.slice(0, 3), ...place.slice(0, 1)] : [salon.displayName, ...place];
  const open = [status?.label, nextFree ? `Next free ${nextFree}` : null].filter(Boolean).join(" · ");
  const pictures: readonly HeroImage[] =
    custom?.images.length ? custom.images : salon.coverImageUrl ? [{ url: salon.coverImageUrl, altText: "" }] : [];
  const carousel = useHeroCarousel(pictures.length);
  const slides = useId();

  return (
    <section aria-labelledby="salon-headline" className={cn(page, "flex flex-col gap-10")}>
      <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-16">
        <div className="flex flex-col gap-7">
          <p className="animate-arrive flex items-center gap-3 text-sm font-medium text-subtle">
            <span aria-hidden="true" className="h-px w-10 shrink-0 bg-sage" />
            {line.join(" · ")}
          </p>
          <h1
            id="salon-headline"
            className="headline max-w-[13ch] text-[clamp(3.5rem,8vw,7.5rem)] leading-[0.95] text-balance text-heading"
          >
            <Accented
              text={custom?.headline ?? salon.tagline ?? salon.displayName}
              highlight={custom?.headline ? custom.highlight : null}
            />
          </h1>
          <p className="max-w-[40ch] text-[17px] leading-[1.7] text-content md:text-[19px]">
            {custom?.text ?? PROMISE}
          </p>
          <div className="flex flex-wrap items-center gap-3.5">
            <Link ref={book} to="/book" viewTransition className={cn(pill.base, pill.ink, pill.arrow, focusRing)}>
              {custom?.buttonLabel ?? "Book an appointment"}
              <ArrowDisc on="ink" />
            </Link>
            <Link to="/services" viewTransition className={cn(pill.base, pill.outline, "h-[3.375rem] px-[1.375rem] text-base", focusRing)}>
              Explore the menu
            </Link>
          </div>
          {stats.length > 0 ? (
            <dl aria-label="In numbers" className="flex flex-wrap gap-x-8 gap-y-5 pt-5">
              {stats.slice(0, 3).map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <dd className="headline order-1 text-[2.5rem] leading-tight text-heading">
                    {stat.value}
                    {stat.star ? (
                      <span aria-hidden="true" className="text-highlight">
                        ★
                      </span>
                    ) : null}
                  </dd>
                  <dt className="order-2 text-sm text-subtle">{stat.label}</dt>
                </div>
              ))}
            </dl>
          ) : null}
        </div>

        <div className="mx-auto w-full max-w-[35rem]">
        <div className="relative aspect-[5/6] w-full">
          <span aria-hidden="true" className="animate-morph absolute inset-[6%_-6%_-4%_10%] bg-sage-tint" />
          <Arch salon={salon} pictures={pictures} carousel={carousel} slides={slides} />
          {inset ? <Inset image={inset} /> : null}
          {open ? (
            <p
              className={cn(
                "animate-arrive absolute bottom-[18%] -left-3 flex items-center gap-2.5 rounded-full bg-base px-[1.125rem] py-3 text-sm font-medium text-heading shadow-float [animation-delay:400ms]",
                status && !status.open && "text-subtle",
              )}
            >
              <span
                aria-hidden="true"
                className={cn("block size-2.5 rounded-full", status?.open === false ? "bg-edge" : "animate-breathe bg-sage")}
              />
              {open}
            </p>
          ) : null}
        </div>
        {pictures.length > 1 ? <HeroCarouselControls controls={slides} carousel={carousel} /> : null}
        </div>
      </div>

      {lastVisit}
    </section>
  );
}

/**
 * The headline, each word rising in turn, with the salon's chosen words in the highlight and in
 * italic, or its last word when it chose none (BR-SA-037: the server only accepts words that are in
 * the headline). To a screen reader it is one line of text: the words are spans with ordinary spaces
 * between them.
 */
function Accented({ text, highlight }: { text: string; highlight: string | null }) {
  const trimmed = text.trim();
  const at = highlight ? trimmed.toLowerCase().indexOf(highlight.trim().toLowerCase()) : -1;
  const [from, to] =
    highlight && at >= 0
      ? [at, at + highlight.trim().length]
      : [trimmed.lastIndexOf(" ") + 1, trimmed.length];

  // Where each word starts, so a word inside the highlighted stretch is marked.
  const words: { word: string; lit: boolean }[] = [];
  let index = 0;
  for (const word of trimmed.split(/\s+/)) {
    const start = trimmed.indexOf(word, index);
    index = start + word.length;
    words.push({ word, lit: start >= from && index <= to });
  }

  return (
    <>
      {words.map(({ word, lit }, position) => (
        // The space is a sibling of the words, not inside a wrapper: an accessible name drops a
        // wrapper's leading space, which would run the words together.
        <Fragment key={`${word}-${position}`}>
          {position > 0 ? " " : null}
          <span
            className={cn("animate-word", lit && "text-highlight italic")}
            style={{ "--word-delay": `${120 + position * STAGGER_MS}ms` } as CSSProperties}
          >
            {word}
          </span>
        </Fragment>
      ))}
    </>
  );
}

/**
 * The salon's hero pictures, else its cover, else a pale sage arch with its monogram. One picture
 * stands still, exactly as before FR-SA-205; several take turns (FR-SA-206).
 */
function Arch({
  salon,
  pictures,
  carousel,
  slides,
}: {
  salon: SalonProfile;
  pictures: readonly HeroImage[];
  carousel: HeroCarousel;
  slides: string;
}) {
  const frame = "arch absolute inset-[0_10%_8%_0] overflow-hidden shadow-soft";
  const [first] = pictures;
  if (pictures.length > 1) {
    return (
      <div className={cn(frame, "bg-muted-bg")}>
        <HeroSlides id={slides} images={pictures} carousel={carousel} />
      </div>
    );
  }
  if (first) {
    return (
      <div className={cn(frame, "bg-muted-bg")}>
        <img src={first.url} alt={first.altText} fetchPriority="high" className="animate-settle size-full object-cover" />
      </div>
    );
  }
  return (
    <div aria-hidden="true" className={cn(frame, "grid place-items-center bg-muted-bg text-sage")}>
      <span className="headline text-[clamp(6rem,16vw,11rem)]">{initials(salon.displayName)}</span>
    </div>
  );
}

/** The second picture, round, ringed in the ground so it reads as laid over the arch's foot. Decorative. */
function Inset({ image }: { image: HeroImage }) {
  return (
    <div className="animate-arrive absolute right-0 bottom-0 aspect-square w-[38%] overflow-hidden rounded-full border-8 border-[var(--t-base)] bg-muted-bg [animation-delay:500ms]">
      <img src={image.url} alt="" loading="lazy" className="size-full object-cover" />
    </div>
  );
}
